import { AxiosError } from 'axios';
import { useState } from 'react';

import { ILocumUserData } from '../../../models/auth/signIn.model';
import { ErrorHttpResponse, HttpConfig } from '../../../models/https';
import { ReferralSummary } from '../../../models/referral/referral.model';
import { normalizeReferralSummary } from '../../../models/referral/referral.adapters';
import { referralActions } from '../../../redux/slices/referralSlice';
import { useAppDisPatch, useAppSelector } from '../../../redux/store';
import useHttps from '../../useHttps';
import { isTierThreeUser } from '../../../utils/referral';

const buildFallbackReferralSummary = (
  authData?: ILocumUserData | null
): ReferralSummary => {
  const isCompanyAccount = Boolean(authData?.companyId);
  const hasEligibleStatus = isCompanyAccount || authData?.status === 'ACTIVE';

  return {
    isEligible: Boolean(
      authData?.isVerified &&
        isTierThreeUser(authData?.tier) &&
        hasEligibleStatus
    ),
    referralCode: authData?.referralCode ?? '',
    referralLink: '',
    points: authData?.referralPoints ?? 0,
    referralCount: 0,
    pendingReferralCount: 0,
    qualifiedReferralCount: 0,
    leaderboardRank: authData?.referralRank ?? null,
  };
};

const useGetReferralSummary = () => {
  const request = useHttps();
  const dispatch = useAppDisPatch();
  const { authData } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);

  const onSuccess = (response: unknown) => {
    const fallback = buildFallbackReferralSummary(authData);

    dispatch(
      referralActions.setReferralSummary(
        normalizeReferralSummary(response, fallback)
      )
    );
    dispatch(referralActions.setReferralSummaryError(null));
  };

  const onError = (err: AxiosError<ErrorHttpResponse>) => {
    console.error(
      err?.message ?? 'Something went wrong while fetching referral summary'
    );
    dispatch(
      referralActions.setReferralSummary(null)
    );
    dispatch(
      referralActions.setReferralSummaryError(
        'Live referral summary is not available yet. Please try again later.'
      )
    );
  };

  const getReferralSummary = () => {
    const url: HttpConfig = {
      url: 'auth-service/users/me/referral',
      method: 'get',
    };

    return request(url, onSuccess, setLoading, onError);
  };

  return { getReferralSummary, loading };
};

export default useGetReferralSummary;
